import React, { useState, useContext, useEffect } from "react";
import { Col, Row, Card, CardBody, Button, FormGroup, Label, Input, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { AuthWizardContext } from "../../../context/Context";
import { checkAcronymAvailability } from "../../../helpers/api/condominium";
import WizardError from "./WizardError";
import CondominiumLocationFields from "../../common/CondominiumLocationFields";

const capitalizeFirstLetter = (value) => {
  const text = (value || "").trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";
};

const CondominiumUserForm = ({ register, errors, setValue }) => {
  const { user, setUser } = useContext(AuthWizardContext);
  const [name, setName] = useState("");
  const [identification, setIdentification] = useState("");
  const [acronym, setAcronym] = useState("");
  const [properties, setProperties] = useState("");
  const [address, setAddress] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [baseCurrency, setBaseCurrency] = useState("USD");

  const [localErrors, setLocalErrors] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [acronymStatus, setAcronymStatus] = useState(null); // null | 'checking' | 'available' | 'taken'

  useEffect(() => {
    if (!user.condominiums) {
      setUser((prev) => ({ ...prev, condominiums: [] }));
    }
  }, [user.condominiums, setUser]);

  // Cuando el usuario llega aquí por un error de acrónimo duplicado al registrarse, precarga ese condominio en edición
  useEffect(() => {
    const [fieldError, ...restFieldErrors] = user._condoFieldErrors || [];
    const condo = fieldError && user.condominiums && user.condominiums[fieldError.index];
    if (!condo) return;

    setName(condo.name);
    setIdentification(condo.identification);
    setAcronym(condo.acronym);
    setProperties(String(condo.properties));
    setAddress(condo.address);
    setState(condo.state || "");
    setCity(condo.city || "");
    setBaseCurrency(condo.base_currency || "USD");
    setEditingIndex(fieldError.index);
    setLocalErrors({ [fieldError.field]: fieldError.message });

    setUser((prev) => {
      const { _condoFieldErrors, ...rest } = prev;
      return restFieldErrors.length > 0 ? { ...rest, _condoFieldErrors: restFieldErrors } : rest;
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    const trimmed = acronym.trim().toUpperCase();
    const isValidFormat = trimmed.length >= 3 && trimmed.length <= 8 && /^[A-Z0-9]+$/.test(trimmed);

    if (!isValidFormat) {
      setAcronymStatus(null);
      return;
    }

    // El mismo acrónimo que ya tenía este condominio antes de editarlo no necesita revalidarse
    if (editingIndex !== null && user.condominiums?.[editingIndex]?.acronym === trimmed) {
      setAcronymStatus('available');
      return;
    }

    const duplicateInList = (user.condominiums || []).some(
      (condo, idx) => idx !== editingIndex && condo.acronym === trimmed
    );
    if (duplicateInList) {
      setAcronymStatus('taken');
      return;
    }

    setAcronymStatus('checking');
    const timeoutId = setTimeout(async () => {
      try {
        const available = await checkAcronymAvailability(trimmed);
        setAcronymStatus(available ? 'available' : 'taken');
      } catch (e) {
        setAcronymStatus(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [acronym, editingIndex, user.condominiums]);

  useEffect(() => {
    register("condominiums", {
      validate: () => {
        return (user.condominiums && user.condominiums.length > 0) || "Debe agregar al menos un condominio para continuar.";
      }
    });
  }, [register, user.condominiums]);

  useEffect(() => {
    setValue("condominiums", user.condominiums || []);
  }, [user.condominiums, setValue]);

  const validateSingle = () => {
    const errs = {};
    if (!name || name.trim().length < 4) {
      errs.name = "El nombre del condominio debe tener al menos 4 caracteres.";
    }

    if (!identification || identification.trim().length < 3) {
      errs.identification = "El documento de identificación debe tener al menos 3 caracteres.";
    } else if (!/^[a-zA-Z0-9_-]+$/i.test(identification)) {
      errs.identification = "Solo se aceptan alfanuméricos y (-).";
    }

    if (!acronym || acronym.trim().length < 3 || acronym.trim().length > 8) {
      errs.acronym = "El acrónimo debe tener entre 3 y 8 caracteres.";
    } else if (!/^[A-Z0-9]+$/i.test(acronym)) {
      errs.acronym = "Solo se aceptan letras mayúsculas y números.";
    } else if (acronymStatus === 'taken') {
      errs.acronym = "Este acrónimo ya está en uso, por favor elige otro.";
    } else if (acronymStatus === 'checking') {
      errs.acronym = "Estamos verificando el acrónimo, espera un momento.";
    }

    const propNum = parseInt(properties, 10);
    if (!properties || isNaN(propNum) || propNum < 1) {
      errs.properties = "El número de propiedades debe ser al menos 1.";
    }

    if (!address || address.trim().length < 10) {
      errs.address = "La dirección debe contener al menos 10 caracteres.";
    }

    if (!state || !state.trim()) {
      errs.state = "El estado es requerido.";
    }

    if (!city || !city.trim()) {
      errs.city = "La ciudad es requerida.";
    }

    setLocalErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const resetForm = () => {
    setName("");
    setIdentification("");
    setAcronym("");
    setProperties("");
    setAddress("");
    setState("");
    setCity("");
    setBaseCurrency("USD");
    setLocalErrors({});
    setEditingIndex(null);
    setAcronymStatus(null);
  };

  const handleAddCondominium = () => {
    if (!validateSingle()) return;

    const condoData = {
      name: name.trim(),
      identification: identification.trim(),
      acronym: acronym.trim(),
      properties: parseInt(properties, 10),
      address: address.trim(),
      state: capitalizeFirstLetter(state),
      city: capitalizeFirstLetter(city),
      base_currency: baseCurrency,
    };

    setUser((prev) => {
      const condominiums = [...(prev.condominiums || [])];
      if (editingIndex !== null) {
        condominiums[editingIndex] = condoData;
      } else {
        condominiums.push(condoData);
      }
      return { ...prev, condominiums };
    });

    resetForm();
  };

  const handleEditCondominium = (index) => {
    const condo = user.condominiums[index];
    setName(condo.name);
    setIdentification(condo.identification);
    setAcronym(condo.acronym);
    setProperties(String(condo.properties));
    setAddress(condo.address);
    setState(condo.state || "");
    setCity(condo.city || "");
    setBaseCurrency(condo.base_currency || "USD");
    setLocalErrors({});
    setEditingIndex(index);
  };

  const handleRemoveCondominium = (indexToRemove) => {
    setUser((prev) => ({
      ...prev,
      condominiums: (prev.condominiums || []).filter((_, index) => index !== indexToRemove),
    }));
    if (editingIndex === indexToRemove) {
      resetForm();
    }
  };

  return (
    <>
      <div className="text-center mb-4">
        <h4 className="text-primary font-weight-bold">Detalles del Condominio</h4>
        <p className="text-muted fs--1">
          Registre los condominios que administrará. Debe agregar <strong>al menos uno</strong> para continuar.
        </p>
      </div>

      {user.condominiums && user.condominiums.length > 0 && (
        <div className="mb-4">
          <h6 className="font-weight-semi-bold text-700 mb-2">Condominios agregados ({user.condominiums.length})</h6>
          <Row>
            {user.condominiums.map((condo, idx) => (
              <Col key={idx} md={6} className="mb-3">
                <Card className={classNames("shadow-sm border-0 bg-white h-100", { "border-primary": editingIndex === idx })} style={{ borderRadius: "10px", transition: "transform 0.2s" }}>
                  <CardBody className="p-3 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="text-primary font-weight-bold mb-1 truncate-ellipsis" style={{ maxWidth: "60%" }}>
                          <FontAwesomeIcon icon="home" className="mr-2 text-primary" />
                          {condo.name}
                        </h6>
                        <div>
                          <Button
                            color="link"
                            className="text-primary p-0 border-0 fs--1 mr-2"
                            onClick={() => handleEditCondominium(idx)}
                            style={{ outline: "none" }}
                          >
                            <FontAwesomeIcon icon="edit" />
                          </Button>
                          <Button
                            color="link"
                            className="text-danger p-0 border-0 fs--1"
                            onClick={() => handleRemoveCondominium(idx)}
                            style={{ outline: "none" }}
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </div>
                      </div>
                      <div className="fs--1 text-600 mt-2">
                        <div><strong>ID:</strong> {condo.identification} | <strong>Acrónimo:</strong> {condo.acronym}</div>
                        <div><strong>Propiedades:</strong> {condo.properties} | <strong>Moneda:</strong> {condo.base_currency === "EUR" ? "Euro (EUR)" : "Dólar (USD)"}</div>
                        <div><strong>Estado:</strong> {condo.state} | <strong>Ciudad:</strong> {condo.city}</div>
                        <div className="text-truncate-2 mt-1"><strong>Dirección:</strong> {condo.address}</div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <div className="mb-3">
        <h6 className="text-800 font-weight-bold mb-3">
          <FontAwesomeIcon icon={editingIndex !== null ? "edit" : "plus"} className={classNames("mr-2", { "text-success": editingIndex === null, "text-primary": editingIndex !== null })} />
          {editingIndex !== null ? "Editar condominio" : "Agregar un nuevo condominio"}
        </h6>

        <FormGroup>
          <Label for="name">Nombre del condominio*</Label>
          <Input
            id="name"
            type="text"
            placeholder="Nombre del condominio"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={localErrors.name ? "border-danger" : ""}
          />
          {localErrors.name && <div className="text-danger fs--1 mt-1">{localErrors.name}</div>}
        </FormGroup>

        <FormGroup>
          <Label for="identification">Documento de identificación (RIF / ID)*</Label>
          <Input
            id="identification"
            type="text"
            placeholder="Ej: J-12345678-9"
            value={identification}
            onChange={(e) => setIdentification(e.target.value)}
            className={localErrors.identification ? "border-danger" : ""}
          />
          {localErrors.identification && <div className="text-danger fs--1 mt-1">{localErrors.identification}</div>}
        </FormGroup>

        <FormGroup>
          <Label for="acronym">Acrónimo*</Label>
          <Input
            id="acronym"
            type="text"
            maxLength="8"
            placeholder="Ej: RESIDENC"
            value={acronym}
            onChange={(e) => {
              setAcronym(e.target.value.toUpperCase());
              setLocalErrors((prev) => ({ ...prev, acronym: undefined }));
            }}
            className={localErrors.acronym || acronymStatus === 'taken' ? "border-danger" : ""}
          />
          {localErrors.acronym && <div className="text-danger fs--1 mt-1">{localErrors.acronym}</div>}
          {!localErrors.acronym && acronymStatus === 'checking' && (
            <div className="text-600 fs--1 mt-1">
              <Spinner size="sm" className="mr-1" />
              Verificando disponibilidad...
            </div>
          )}
          {!localErrors.acronym && acronymStatus === 'taken' && (
            <div className="text-danger fs--1 mt-1">
              Este acrónimo ya está en uso, por favor elige otro.
            </div>
          )}
          {!localErrors.acronym && acronymStatus === 'available' && (
            <div className="text-success fs--1 mt-1">
              <FontAwesomeIcon icon="check" className="mr-1" />
              Acrónimo disponible.
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for="properties">Número de propiedades (Casas / Aptos)*</Label>
          <Input
            id="properties"
            type="number"
            min="1"
            placeholder="Ej: 50"
            value={properties}
            onChange={(e) => setProperties(e.target.value)}
            className={localErrors.properties ? "border-danger" : ""}
          />
          {localErrors.properties && <div className="text-danger fs--1 mt-1">{localErrors.properties}</div>}
        </FormGroup>

        <FormGroup>
          <Label for="base_currency">Moneda de trabajo*</Label>
          <Input
            id="base_currency"
            type="select"
            value={baseCurrency}
            onChange={(e) => setBaseCurrency(e.target.value)}
          >
            <option value="USD">Dólar (USD)</option>
            <option value="EUR">Euro (EUR)</option>
          </Input>
          <div className="text-600 fs--1 mt-1">
            Todos los montos de este condominio quedarán expresados en esta
            moneda. No podrá cambiarse una vez que el condominio tenga
            actividad registrada.
          </div>
        </FormGroup>

        <CondominiumLocationFields
          state={state}
          city={city}
          onStateChange={(value) => {
            setState(value);
            setCity("");
          }}
          onCityChange={setCity}
          stateError={localErrors.state}
          cityError={localErrors.city}
        />

        <FormGroup>
          <Label for="address">Dirección del condominio*</Label>
          <Input
            id="address"
            type="textarea"
            rows="3"
            placeholder="Ingrese la dirección exacta..."
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className={localErrors.address ? "border-danger" : ""}
          />
          {localErrors.address && <div className="text-danger fs--1 mt-1">{localErrors.address}</div>}
        </FormGroup>

        <div className="text-right">
          {editingIndex !== null && (
            <Button
              color="link"
              type="button"
              className="px-4 font-weight-semi-bold text-600"
              onClick={resetForm}
            >
              Cancelar edición
            </Button>
          )}
          <Button
            color={editingIndex !== null ? "primary" : "success"}
            type="button"
            className="px-4 font-weight-semi-bold text-white shadow-sm"
            onClick={handleAddCondominium}
          >
            <FontAwesomeIcon icon={editingIndex !== null ? "save" : "plus"} className="mr-2" />
            {editingIndex !== null ? "Guardar cambios" : "Agregar a la lista"}
          </Button>
        </div>
      </div>

      {errors.condominiums && (
        <div className="mb-3">
          <WizardError error={errors.condominiums.message} className="text-center" />
        </div>
      )}
    </>
  );
};

export default CondominiumUserForm;
